import { useEffect, useState } from "react";
import api from '../config/api';

export default function CreateTransactionModal({ show, onClose, onSuccess }) {
  const [form, setForm] = useState({
    debit_account_code_id: "",
    credit_account_code_id: "",
    description: "",
    amount: "",
    transaction_date: "",
    reference_number: "",
    payment_proof_url: "",
    supplier_name: "",
    correction_for: null,
  });

  const [accountCodes, setAccountCodes] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [closing, setClosing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    const fetchAccountCodes = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(api("/account_codes/"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setAccountCodes(data);
      } catch (err) {
        console.error("Failed to fetch account codes:", err);
      }
    };
    fetchAccountCodes();
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG, JPEG, or PNG files are allowed.");
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("File size must be less than 2MB.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "tov_finance_upload");

    setUploading(true);
    setUploadProgress(0);

    try {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://api.cloudinary.com/v1_1/djskqs0h5/image/upload");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = (event.loaded / event.total) * 100;
          setUploadProgress(Math.round(percentComplete));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          const data = JSON.parse(xhr.responseText);
          setForm((prev) => ({ ...prev, payment_proof_url: data.secure_url || "" }));
        } else {
          alert("Image upload failed.");
        }
        setUploading(false);
      };

      xhr.onerror = () => {
        alert("Image upload failed.");
        setUploading(false);
      };

      xhr.send(formData);
    } catch (err) {
      console.error("Upload failed", err);
      alert("Image upload failed.");
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    const user_id = JSON.parse(atob(token.split(".")[1]))?.user_id;

    const payload = {
      ...form,
      user_id,
      amount: Number(form.amount),
      debit_account_code_id: Number(form.debit_account_code_id),
      credit_account_code_id: Number(form.credit_account_code_id),
      transaction_date: new Date(form.transaction_date).toISOString(),
      correction_for: form.correction_for || null,
    };

    try {
      const response = await fetch(api("/transactions/double-entry"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json();
        setErrorMessage(err.error || "Failed to create transaction");
        return;
      }

      onSuccess();
      setClosing(true);
      setTimeout(() => {
        setClosing(false);
        onClose();
      }, 200);
      setErrorMessage("");
    } catch (err) {
      console.error(err);
      setErrorMessage("Something went wrong.");
    }
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 200);
  };

  if (!show) return null;

  return (
    <div className="modal fade-modal show d-block" style={{ background: "rgba(0,0,0,0.5)", opacity: closing ? 0 : 1, transition: "opacity 0.2s ease" }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">Create Double Entry Transaction</h5>
              <button type="button" className="btn-close" onClick={handleClose}></button>
            </div>
            <div className="modal-body">
              {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Debit Account Code</label>
                    <select className="form-select" name="debit_account_code_id" value={form.debit_account_code_id} onChange={handleChange} required>
                      <option value="">-- Select Debit --</option>
                      {accountCodes.map((ac) => (
                        <option key={ac.id} value={ac.id}>{ac.code} - {ac.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Credit Account Code</label>
                    <select className="form-select" name="credit_account_code_id" value={form.credit_account_code_id} onChange={handleChange} required>
                      <option value="">-- Select Credit --</option>
                      {accountCodes.map((ac) => (
                        <option key={ac.id} value={ac.id}>{ac.code} - {ac.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Amount</label>
                    <div className="input-group">
                      <span className="input-group-text">Rp.</span>
                      <input type="number" className="form-control" name="amount" value={form.amount} onChange={handleChange} required />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Transaction Date</label>
                    <input type="datetime-local" className="form-control" name="transaction_date" value={form.transaction_date} onChange={handleChange} required />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <input type="text" className="form-control" name="description" value={form.description} onChange={handleChange} />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Reference Number</label>
                    <input type="text" className="form-control" name="reference_number" value={form.reference_number} onChange={handleChange} required />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Upload Payment Proof</label>
                    <input type="file" className="form-control" accept="image/*" onChange={handleFileChange} disabled={uploading} />
                    {uploading && <div className="mt-2"><div className="progress"><div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${uploadProgress}%` }}></div></div></div>}
                    {form.payment_proof_url && (
                      <div className="mt-2">
                        <a href={form.payment_proof_url} target="_blank" rel="noreferrer">View Uploaded Image</a>
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Supplier Name (Optional)</label>
                    <input type="text" className="form-control" name="supplier_name" value={form.supplier_name} onChange={handleChange} />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" type="button" onClick={handleClose}>Cancel</button>
              <button className="btn btn-primary" type="submit">Submit</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
